import { lazy, Suspense } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Target,
  Sparkles,
  Type,
  MessageSquare,
  BookOpen,
  Layers,
  Image,
  Users,
  Globe,
  Sparkles as SparklesIcon,
  BadgeCheck as BadgeCheckIcon,
  TrendingUp as TrendingUpIcon,
} from "lucide-react";
import { GlowCard } from "@/components/ui/spotlight-card";
import { ShineBorder } from "@/components/ui/shine-border";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { BrandHeritageSection } from "@/components/sections/BrandHeritageSection";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";
import { projectsByCategoryQueryOptions } from "@/lib/portfolio";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

const SelectedWork = lazy(() =>
  import("@/components/portfolio/SelectedWork").then((m) => ({ default: m.SelectedWork })),
);

export const Route = createFileRoute("/branding")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(projectsByCategoryQueryOptions("branding")),

  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Branding & Brand Identity — Mind Media" },
      {
        name: "description",
        content:
          "Strategy, identity, and a complete visual system from a senior creative director. The kind of brand that earns trust before you say a word.",
      },
      { property: "og:title", content: "Branding & Brand Identity — Mind Media" },
      {
        property: "og:description",
        content:
          "A logo is not a brand. Let's build the real thing — strategy, identity, voice, and guidelines.",
      },
      { property: "og:url", content: "https://mindmedia.co/branding" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/branding" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Branding & Brand Identity",
          serviceType: "Brand Strategy and Visual Identity",
          provider: { "@type": "Organization", name: "Mind Media", url: "https://mindmedia.co" },
          areaServed: "Worldwide",
          url: "https://mindmedia.co/branding",
          description:
            "Strategy, identity, and a complete visual system from a senior creative director.",
        }),
      },
    ],
  }),
  component: BrandingPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

function BrandingPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-10 text-center sm:pb-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-4 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:px-6 sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap">
            <span className="block sm:inline">A logo is not a brand. </span>
            <span
              className={`${gradText} inline-block w-max whitespace-nowrap text-[2.0625rem] leading-tight sm:block sm:w-auto sm:whitespace-normal sm:text-5xl lg:inline lg:whitespace-nowrap lg:text-[clamp(1.5rem,3.5vw,2.75rem)]`}
            >
              Let's build the real thing.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-5xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            <span className="inline sm:block">
              Strategy, identity, and a complete visual system from a senior creative director with
              20+ years of brand work.
            </span>{" "}
            <span className="inline sm:block">
              The kind of brand that earns trust before you say a word, and lets you charge what
              you're worth.
            </span>
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Book a Brand Consult <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#work"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              View Work
            </a>
          </div>
          <div className="mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {[
              "Senior creative direction",
              "Strategy, not a stock logo",
              "A complete identity system",
            ].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                  <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                </span>
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO + WHAT GREAT BRANDING DOES */}
      <section className="px-6 pt-10 pb-12 lg:pb-16">
        <div className="mx-auto max-w-7xl lg:px-4">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <ShareloEmbed id="32UKQC8ocYlsnpzHUMCSh" title="Why us — customer story" />

            <div className="flex flex-col gap-8">
              <ShineBorder
                borderRadius={16}
                borderWidth={2}
                duration={10}
                color={["#3b82f6", "#a855f7", "#ec4899"]}
                className="w-full max-w-[32rem]"
              >
                <GlowCard
                  glowColor="blue"
                  disableInnerGlow
                  customSize
                  className="relative w-full bg-card"
                >
                  <div className="py-4 px-6 sm:py-5 sm:px-8">
                    <h3 className="text-center text-[1.7rem] font-bold tracking-tight text-foreground sm:text-[1.7rem]">
                      <span className="block sm:inline">What great</span>{" "}
                      <span className="block sm:inline">
                        <span className="inline whitespace-nowrap">
                          branding
                          <span className="inline sm:hidden">{"\u00A0"}</span>
                          <span className="hidden sm:inline"> </span>
                          <span className="text-gradient-cycle">actually does</span>
                        </span>
                      </span>
                    </h3>
                    <ul className="mt-2">
                      {[
                        {
                          title: "Makes you instantly recognizable",
                          description: "A cohesive identity people remember after one look.",
                          Icon: SparklesIcon,
                          bgClass: "bg-[#3b82f6]/10",
                          textClass: "text-[#3b82f6]",
                        },
                        {
                          title: "Earns trust before you speak",
                          description:
                            "Look the part and buyers assume the quality before they read a word.",
                          Icon: BadgeCheckIcon,
                          bgClass: "bg-[#a855f7]/10",
                          textClass: "text-[#a855f7]",
                        },
                        {
                          title: "Lets you charge a premium",
                          description:
                            "Stop competing on price. A strong brand justifies the number on the invoice.",
                          Icon: TrendingUpIcon,
                          bgClass: "bg-[#ec4899]/10",
                          textClass: "text-[#ec4899]",
                        },
                      ].map(({ title, description, Icon, bgClass, textClass }, i) => (
                        <li key={title}>
                          {i > 0 && (
                            <div className="h-px w-full bg-gradient-to-r from-transparent via-foreground/15 to-transparent" />
                          )}
                          <div className="flex items-start gap-4 py-5">
                            <span
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${bgClass}`}
                            >
                              <Icon className={`h-6 w-6 ${textClass}`} strokeWidth={2.25} />
                            </span>
                            <div className="text-left">
                              <p className="text-lg text-foreground font-semibold">{title}</p>
                              <p className="text-sm leading-relaxed text-muted-foreground">
                                {description}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </GlowCard>
              </ShineBorder>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Why most branding falls flat"
            maxWidth="max-w-6xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                A cheap logo gets you a graphic. <span className={gradText}>Not a brand.</span>
              </>
            }
          >
            Branding isn't a picture, it's the gut feeling people get about you. Here's where most
            small businesses get it wrong.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: "A logo",
                h: "Isn't an identity",
                p: "A mark off a marketplace gives you one graphic. It falls apart the moment you need it to work across everything you do.",
                Icon: Image,
              },
              {
                n: "Looking like",
                h: "Everyone else",
                p: "Template branding makes you blend into your category, exactly when you need to stand out and justify a premium price.",
                Icon: Users,
              },
              {
                n: "Inconsistent",
                h: "Everywhere you show up",
                p: "Different fonts and colors on your site, social, and print quietly tells customers you're not quite buttoned up.",
                Icon: Globe,
              },
            ].map(({ n, h, p, Icon }) => (
              <div key={h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div className="text-3xl font-bold leading-none tracking-tight text-brand-blue md:text-4xl">
                      {n}
                    </div>
                    <h3 className="mt-0.5 text-lg font-bold leading-tight tracking-tight">{h}</h3>
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED BRANDING WORK */}
      <Suspense fallback={null}>
        <SelectedWork
          category="branding"
          eyebrow="My Work"
          sectionId="work"
          className="pt-10 pb-12 sm:pt-14 sm:pb-16"
          title={
            <span className="whitespace-nowrap text-[2.0625rem] sm:text-4xl lg:text-5xl">
              Brands that <span className={gradText}>stand out.</span>
            </span>
          }
          subtitle={
            <>
              Every identity I create is built to make your <br className="sm:hidden" />
              business unforgettable — not just look pretty.
            </>
          }
        />
      </Suspense>

      {/* WHAT'S INCLUDED */}
      <section id="includes" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What's included"
            title={
              <>
                A complete brand, <span className={gradText}>built to last.</span>
              </>
            }
          >
            Not a logo and a handshake. A full identity system you can run your whole business on.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Target,
                h: "Brand strategy & positioning",
                p: "Who you are, who you're for, and why you're different, nailed down before a single pixel is drawn.",
              },
              {
                icon: Sparkles,
                h: "Logo & visual identity",
                p: "A primary logo, variations, and marks designed to look sharp everywhere from a sign to a favicon.",
              },
              {
                icon: Type,
                h: "Color & typography",
                p: "A palette and type system that's unmistakably yours and works in print and on screen.",
              },
              {
                icon: MessageSquare,
                h: "Brand voice & messaging",
                p: "How you sound, your tagline, and the core messages so your words feel as intentional as your look.",
              },
              {
                icon: BookOpen,
                h: "Brand guidelines",
                p: "A clear playbook so anyone touching your brand, including future you, keeps everything consistent.",
              },
              {
                icon: Layers,
                h: "Launch collateral",
                p: "Business cards, social templates, and decks, the pieces you actually use on day one.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="Why us"
            title={
              <span className="lg:whitespace-nowrap">
                This isn't a template.{" "}
                <span className={gradText}>It's senior creative direction.</span>
              </span>
            }
          >
            You're not picking from a gallery of stock logos. You're working directly with a
            creative director who's spent two decades shaping brands, applying that same eye to your
            business. Strategy first, design that commands a premium, and a system that holds
            together everywhere.
          </SectionHead>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "Strategy before design",
                  "We figure out what you stand for first, so the look means something.",
                ],
                ["Built to charge more", "A premium brand lets you stop competing on price."],
                [
                  "One coherent system",
                  "Everything matches, from your site to your business card.",
                ],
                [
                  "You own all of it",
                  "Source files, fonts, and full rights, handed over and yours.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="relative rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="absolute right-6 top-6 text-right">
                <div className="text-3xl font-bold leading-none">20+</div>
                <div className="text-xs uppercase tracking-wider text-white/80">Years Exp</div>
              </div>
              <div className="text-4xl sm:text-6xl tracking-tight leading-none font-bold text-white">
                3 Seconds
              </div>
              <div className="mt-2 text-lg font-bold">to form a first impression</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                People decide how they feel about your business almost instantly, long before they
                read a word. A strong, consistent brand makes those few seconds work for you instead
                of against you, and that judgment follows them through everything else they see.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="px-6 pt-20 pb-10">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <span className="lg:whitespace-nowrap">
                From who you are to <span className={gradText}>how you show up.</span>
              </span>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                n: 1,
                h: "Discover",
                p: "We dig into your business, your audience, and your competition to find what makes you genuinely different.",
              },
              {
                n: 2,
                h: "Design",
                p: "We shape the logo, color, and type into a few strong directions, then refine the one that's right with you.",
              },
              {
                n: 3,
                h: "System",
                p: "We build it out into a full identity with voice, messaging, and the collateral you'll use every day.",
              },
              {
                n: 4,
                h: "Launch",
                p: "You get the guidelines, the files, and everything you need to put your new brand to work right away.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection className="pt-4 sm:pt-2" hideKicker hideMockupButton meetingButtonPrimary />

      {/* BRAND HERITAGE */}
      <BrandHeritageSection onCtaClick={goToMockup} showDivider={false} />

      {/* PRICING */}
      <section id="pricing" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Investment"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Brand packages for <span className={gradText}>where you are.</span>
              </>
            }
          >
            One-time projects, no surprises. Every brand project starts with a consult, so the scope
            and price fit what you actually need.
          </SectionHead>
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {[
              {
                h: "Brand Essentials",
                desc: "For a sharp, clean start",
                amt: "$2,500",
                sub: "Logo and the core of an identity",
                b: [
                  "Primary logo & variations",
                  "Color & type system",
                  "Mini guidelines",
                  "All source files",
                ],
                cta: "ghost",
                feat: false,
              },
              {
                h: "Full Brand Identity",
                desc: "The complete system",
                amt: "$6,500",
                sub: "Strategy, identity, voice, and guidelines",
                b: [
                  "Everything in Essentials",
                  "Brand strategy & positioning",
                  "Voice & messaging",
                  "Full guidelines & launch collateral",
                ],
                cta: "primary",
                feat: true,
              },
              {
                h: "Brand + Website",
                desc: "Brand and site as one",
                amt: "Let's talk",
                sub: "The full package, designed together",
                b: [
                  "Everything in Full Identity",
                  "A custom website to match",
                  "One consistent look, end to end",
                  "Priority timeline",
                ],
                cta: "ghost",
                feat: false,
              },
            ].map((p) => (
              <div
                key={p.h}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-card p-8",
                  p.feat
                    ? "border-transparent shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]"
                    : "border-border",
                )}
              >
                {p.feat && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold tracking-tight">{p.h}</h3>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <div className="mt-5 text-4xl font-extrabold tracking-tight">{p.amt}</div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">{p.sub}</div>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToMockup}
                  className={cn(
                    "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                    p.cta === "primary"
                      ? "bg-gradient-to-r from-brand-blue to-[#ec4899] text-white shadow-lg shadow-brand-blue/40 hover:opacity-90"
                      : "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10",
                  )}
                >
                  {p.amt === "Let's talk" ? "Book a call" : "Start here"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <>
                Before you <span className={gradText}>start.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "What's the difference between a logo and a brand?",
                "A logo is one graphic. A brand is the whole impression, the strategy, the look, the voice, and how it all stays consistent. We build the brand. The logo is one piece of it.",
              ],
              [
                "How long does a brand project take?",
                "A few weeks for most projects, depending on scope. Strategy and a couple of rounds of refinement take real time, because rushing it is how you end up with something forgettable.",
              ],
              [
                "What files do I get?",
                "All of them. Logo files in every format you'll need, your fonts and colors, and the guidelines. Full ownership, handed over so you're never stuck asking for your own assets.",
              ],
              [
                "Can you do my website too?",
                "Yes, and it's the best way to do it. When the brand and the site are designed together, everything lines up. The Brand + Website package covers exactly that.",
              ],
              [
                "How many revisions do I get?",
                "Enough to get it right. We present strong directions and refine the chosen one with you, rather than throwing endless options and hoping something sticks.",
              ],
              [
                "Why does it cost more than a logo site?",
                "Because you're paying for senior creative direction and strategy, not a stock template. A brand that lets you charge a premium pays for itself many times over.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <ContactSection
        compact
        ctaText="Book a brand consult"
        heading={
          <h2 className="text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
            Build a brand people{" "}
            <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
              remember.
            </span>
          </h2>
        }
        subheading={
          <p className="mt-6 text-lg text-muted-foreground">
            Book a brand consult and we'll talk through where you are, where you want to be seen,
            and what it takes to get there.
          </p>
        }
      />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/branding", label: "Branding" },
          { href: "/seo", label: "SEO" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
